import React, { useState, useEffect, useCallback } from 'react';
import { Store, Save, AlertCircle } from 'lucide-react';
import { supabase } from './supabaseClient';
import { useTenant } from './context/TenantContext';

const StoreSettings = ({ user, userPermissions }) => {
  const { tenant } = useTenant();
  const [config, setConfig]         = useState(null);
  const [form, setForm]             = useState({});
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [toast, setToast]           = useState('');
  const [toastError, setToastError] = useState(false);

  // Toast mechanism mirrors CustomsSettings.js — no new dependency.
  const showToast = useCallback((msg, isError = false) => {
    setToast(msg);
    setToastError(isError);
    setTimeout(() => setToast(''), 4000);
  }, []);

  const canEdit = !!userPermissions?.canEditCustomsConfig;

  // ── Load the tenant row on mount ───────────────────────────────────────────
  const load = useCallback(async () => {
    if (!tenant?.id) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('tenants')
        .select('id, company_name, office_address, office_phone')
        .eq('id', tenant.id)
        .maybeSingle();
      if (error) throw error;
      setConfig(data);
      if (data) {
        setForm({
          office_address: data.office_address ?? '',
          office_phone:   data.office_phone   ?? '',
        });
      }
      console.log('[storeSettings] loaded — tenant:', tenant.id);
    } catch (err) {
      console.error('[storeSettings] load error:', err);
      showToast('Error al cargar datos de tienda.', true);
    } finally {
      setLoading(false);
    }
  }, [tenant?.id, showToast]);

  useEffect(() => {
    load();
  }, [load]);

  // ── Save — UPDATE the tenant row, verify RLS actually wrote a row ───────────
  const handleSave = async () => {
    if (!tenant?.id) return;
    setSaving(true);
    try {
      const office_address = (form.office_address ?? '').trim() || null;
      const office_phone   = (form.office_phone   ?? '').trim() || null;

      const { data, error } = await supabase
        .from('tenants')
        .update({ office_address, office_phone })
        .eq('id', tenant.id)
        .select();
      if (error) throw error;

      // RLS returns zero rows on a policy block WITHOUT raising an error.
      // An empty/missing array means the write was silently denied.
      if (!data || data.length === 0) {
        showToast('No se pudo guardar. No tenés permiso para editar esta tienda.', true);
        return;
      }

      setConfig(prev => ({ ...prev, office_address, office_phone }));
      setForm({
        office_address: office_address ?? '',
        office_phone:   office_phone   ?? '',
      });
      console.log('[storeSettings] saved — by:', user?.username || 'unknown');
      showToast('Datos de tienda guardados exitosamente.');
    } catch (err) {
      console.error('[storeSettings] save error:', err);
      showToast('Error al guardar: ' + (err.message || ''), true);
    } finally {
      setSaving(false);
    }
  };

  // ── Loading / not found states ─────────────────────────────────────────────
  if (loading) {
    return (
      <div className="tile max-w-2xl mx-auto" style={{ padding: '40px', textAlign: 'center' }}>
        <span className="metric-label">Cargando datos de tienda…</span>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="tile max-w-2xl mx-auto" style={{ padding: '40px', textAlign: 'center' }}>
        <AlertCircle size={28} style={{ margin: '0 auto 8px', opacity: 0.4 }} />
        <span className="metric-label">No se encontró la tienda. Contacte al administrador.</span>
      </div>
    );
  }

  const labelStyle = {
    fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)',
    textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: '2px',
  };
  const inputStyle = {
    width: '100%', padding: '8px 10px',
    fontSize: '15px', fontWeight: 600,
    border: '1px solid var(--border)', borderRadius: '8px',
    background: 'var(--bg)',
    color: 'var(--text)',
    cursor: canEdit ? 'auto' : 'not-allowed',
    opacity: canEdit ? 1 : 0.6,
  };

  return (
    <div className="max-w-2xl mx-auto">
      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: '16px', right: '16px', zIndex: 9999,
          background: 'var(--bg)', border: '1px solid var(--border)',
          borderRadius: '10px', padding: '12px 20px',
          boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
          fontSize: '14px', fontWeight: 500,
          color: toastError ? 'var(--red, #dc2626)' : 'inherit',
        }}>
          {toast}
        </div>
      )}

      {/* Header */}
      <div className="tile" style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Store size={24} style={{ color: 'var(--blue)', opacity: 0.7 }} />
          <div>
            <h2 style={{ fontSize: '20px', fontWeight: 700, margin: 0 }}>Datos de Tienda</h2>
            <p className="metric-sub" style={{ margin: '4px 0 0' }}>
              Dirección y teléfono de la oficina — aparecen en las facturas de los clientes.
            </p>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="tile">
        {/* Office address — multiline */}
        <div style={{ marginBottom: '20px' }}>
          <label style={labelStyle}>DIRECCIÓN DE OFICINA</label>
          <p className="metric-sub" style={{ margin: '0 0 6px', fontSize: '11px' }}>
            Dirección física de la oficina para la factura
          </p>
          <textarea
            rows={3}
            value={form.office_address ?? ''}
            onChange={e => setForm(prev => ({ ...prev, office_address: e.target.value }))}
            disabled={!canEdit}
            style={{ ...inputStyle, resize: 'vertical', fontWeight: 500, lineHeight: 1.4 }}
          />
        </div>

        {/* Office phone — single line */}
        <div style={{ marginBottom: '4px' }}>
          <label style={labelStyle}>TELÉFONO DE OFICINA</label>
          <p className="metric-sub" style={{ margin: '0 0 6px', fontSize: '11px' }}>
            Teléfono de contacto que aparece en la factura
          </p>
          <input
            type="text"
            value={form.office_phone ?? ''}
            onChange={e => setForm(prev => ({ ...prev, office_phone: e.target.value }))}
            disabled={!canEdit}
            style={{ ...inputStyle, width: '260px', maxWidth: '100%' }}
          />
        </div>

        {/* Save button — always rendered; disabled for non-editors */}
        <div style={{ marginTop: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !canEdit}
            className="btn btn-primary"
            style={{
              fontSize: '14px', padding: '10px 24px',
              display: 'flex', alignItems: 'center', gap: '8px',
              opacity: canEdit ? 1 : 0.45,
              cursor: canEdit ? 'pointer' : 'not-allowed',
            }}
          >
            <Save size={16} />
            {saving ? 'Guardando…' : 'Guardar cambios'}
          </button>
          {!canEdit && (
            <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
              Solo administradores y gerentes pueden editar esta configuración.
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default StoreSettings;
